// src/utils/auth.js
import { clearAllSessionCaches, clearBrowserCaches } from './cache.js';
import { resetSessionHealth, setSessionExpiryFromToken } from './sessionManager.js';
import { getTokenExpiration } from './token.js';

export const LOGOUT_EVENT = 'bluezap:logout';

const SESSION_DEFAULT = {
  companyId: null,
  attendantId: null,
  roleName: null,
  roleId: null,
};

let authSession = { ...SESSION_DEFAULT };

const TOKEN_STATE_DEFAULT = {
  token: undefined,
  accessible: false,
};

let authTokenState = { ...TOKEN_STATE_DEFAULT };

const parseExpiryTimestamp = (value) => {
  if (value === null || value === undefined) {
    return null;
  }
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value > 1e12 ? value : value * 1000;
  }
  if (typeof value === 'string') {
    const parsedIso = Date.parse(value);
    if (!Number.isNaN(parsedIso)) {
      return parsedIso;
    }
    const numericValue = Number(value);
    if (Number.isFinite(numericValue)) {
      return numericValue > 1e12 ? numericValue : numericValue * 1000;
    }
    return null;
  }
  if (value instanceof Date) {
    return value.getTime();
  }
  return null;
};

const updateSessionExpiryFromTokenInput = ({ token, expiresAt, graceMs }) => {
  const explicitExpiry = parseExpiryTimestamp(expiresAt);
  const tokenExpiry = token ? getTokenExpiration(token) : null;
  const finalExpiry = explicitExpiry ?? tokenExpiry;
  if (!finalExpiry) {
    return false;
  }
  return setSessionExpiryFromToken(finalExpiry, { graceMs });
};

const readCookie = (name) => {
  if (typeof document === 'undefined') return null;
  try {
    const entries = document.cookie ? document.cookie.split(';') : [];
    for (const entry of entries) {
      const [key, ...rest] = entry.trim().split('=');
      if (key === name) {
        return decodeURIComponent(rest.join('='));
      }
    }
  } catch {
    return null;
  }
  return null;
};

const normalizeOptionalNumber = (value) => {
  if (value === null || value === undefined) return null;
  const parsed = Number(value);
  return Number.isNaN(parsed) ? null : parsed;
};

const hasOwnField = (obj, key) => Object.prototype.hasOwnProperty.call(obj || {}, key);

const resolveOptionalNumberField = (partial, camelKey, snakeKey, currentValue) => {
  if (hasOwnField(partial, camelKey)) {
    return normalizeOptionalNumber(partial[camelKey]);
  }
  if (hasOwnField(partial, snakeKey)) {
    return normalizeOptionalNumber(partial[snakeKey]);
  }
  return currentValue;
};

const resolveOptionalTextField = (partial, camelKey, snakeKey, currentValue) => {
  if (hasOwnField(partial, camelKey)) {
    return partial[camelKey] ?? null;
  }
  if (hasOwnField(partial, snakeKey)) {
    return partial[snakeKey] ?? null;
  }
  return currentValue;
};

const persistSession = () => {
  // Persistimos apenas identificadores numéricos mínimos para compatibilidade de telas.
  try {
    if (authSession.companyId !== null && authSession.companyId !== undefined) {
      localStorage.setItem('company_id', String(authSession.companyId));
    } else {
      localStorage.removeItem('company_id');
    }
    if (authSession.attendantId !== null && authSession.attendantId !== undefined) {
      localStorage.setItem('attendant_id', String(authSession.attendantId));
    } else {
      localStorage.removeItem('attendant_id');
    }
  } catch {
    // ignore storage failures
  }
};

const hydrateSessionFromStorage = () => {
  try {
    const storedCompanyId = localStorage.getItem('company_id');
    const storedAttendantId = localStorage.getItem('attendant_id');
    const nextSession = { ...authSession };

    if (nextSession.companyId === null || nextSession.companyId === undefined) {
      if (storedCompanyId !== null && storedCompanyId !== undefined) {
        const normalized = normalizeOptionalNumber(storedCompanyId);
        if (normalized !== null) {
          nextSession.companyId = normalized;
        }
      }
    }

    if (nextSession.attendantId === null || nextSession.attendantId === undefined) {
      if (storedAttendantId !== null && storedAttendantId !== undefined) {
        const normalized = normalizeOptionalNumber(storedAttendantId);
        if (normalized !== null) {
          nextSession.attendantId = normalized;
        }
      }
    }

    authSession = nextSession;
  } catch {
    // Keep the in-memory session when storage access is blocked.
  }
  return authSession;
};

export const setAuthSession = (partial = {}) => {
  authSession = {
    ...authSession,
    companyId: resolveOptionalNumberField(partial, 'companyId', 'company_id', authSession.companyId),
    attendantId: resolveOptionalNumberField(
      partial,
      'attendantId',
      'attendant_id',
      authSession.attendantId
    ),
    roleName: resolveOptionalTextField(partial, 'roleName', 'role_name', authSession.roleName),
    roleId: resolveOptionalNumberField(partial, 'roleId', 'role_id', authSession.roleId),
  };
  persistSession();
};

export const getAuthSession = () => {
  hydrateSessionFromStorage();
  return { ...authSession };
};

export const hasStoredAuthSession = () => {
  const snapshot = getAuthSession();
  return snapshot.attendantId !== null && snapshot.attendantId !== undefined;
};

export const hasReadableSessionCookie = () => readCookie('csrf_token') !== null;

export const emitLogoutEvent = () => {
  try {
    window.dispatchEvent(new CustomEvent(LOGOUT_EVENT));
  } catch {
    // ignore in non-browser environments
  }
};

export const setAuthToken = (token, options = {}) => {
  const normalizedToken = typeof token === 'string' ? token : undefined;
  const accessible = Boolean(options.accessible && normalizedToken);
  authTokenState = {
    token: accessible ? normalizedToken : undefined,
    accessible,
  };
  updateSessionExpiryFromTokenInput({
    token: normalizedToken,
    expiresAt: options.expiresAt,
    graceMs: options.graceMs,
  });
};

export const getAuthToken = () => authTokenState.token;

export const removeAuthToken = () => {
  authTokenState = { ...TOKEN_STATE_DEFAULT };
};

export const setAuthRoleSnapshot = (roleName, roleId) => {
  try {
    authSession.roleName = roleName && typeof roleName === 'string' ? roleName : null;
    authSession.roleId = normalizeOptionalNumber(roleId);
    persistSession();
  } catch {
    // ignore storage failures
  }
};

export const clearAuthRoleSnapshot = () => {
  try {
    authSession.roleName = null;
    authSession.roleId = null;
    persistSession();
  } catch {
    // ignore
  }
};

export const isAuthenticated = () => {
  return hasReadableSessionCookie();
};

export const performLogout = () => {
  removeAuthToken();
  resetSessionHealth();
  authSession = { ...SESSION_DEFAULT };
  persistSession();
  try {
    clearAllSessionCaches();
    clearBrowserCaches();
  } catch {
    // Se não conseguir limpar tudo, tenta ao menos remover o token legado
    try {
      sessionStorage.removeItem('session_attendant_id');
      sessionStorage.removeItem('session_company_id');
      sessionStorage.removeItem('session_role_name');
      sessionStorage.removeItem('session_role_id');
    } catch {
      // Storage pode ja estar indisponivel; seguimos limpando o restante.
    }
    try {
      document.cookie = 'csrf_token=; Max-Age=0; path=/';
    } catch {
      // Cookie pode ja ter sido removido.
    }
  }
  clearAuthRoleSnapshot();
  emitLogoutEvent();
  try {
    window.dispatchEvent(
      new CustomEvent('bluezap:auth-changed', {
        detail: { attendantId: null, companyId: null, roleName: null, roleId: null },
      })
    );
  } catch {
    // ignore when window is unavailable
  }
  try {
    localStorage.removeItem('role_name');
    localStorage.removeItem('role_id');
  } catch {
    // ignore
  }
};
